<template>
  <div>
    <page-header
      :title="title"
      :breadcrumb="breadcrumb"
      :container-class="containerClass"
      class="mb-16pt"
      show-back
      info-text="Automated course emails sent to your students."
    />

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
              <b-form-input
                class="w-lg-auto"
                placeholder="Search by recipient or subject"
                v-model="searchTerm"
                @input="onSearch"
              />
              <b-btn variant="flush" type="submit">
                <md-icon>search</md-icon>
              </b-btn>
            </form>
          </div>

          <b-table
            :fields="tableColumns"
            :items="logs"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-border-collapse
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>

            <template #cell(status)="data">
              <span class="badge" :class="statusBadgeClass(data.item.status)">{{ data.item.status }}</span>
            </template>

            <template #cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>

            <template #empty>
              <p class="text-center text-muted my-3">No emails sent yet.</p>
            </template>
          </b-table>

          <div class="card-footer d-flex justify-content-end" v-if="totalLogs > perPage">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalLogs"
              :per-page="perPage"
              @change="onPageChange"
              class="mb-0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { mapActions } from 'vuex';
import { debounce } from 'lodash';
import moment from 'moment';

export default {
  name: 'EmailConfigLogs',
  extends: Page,
  components: { PageHeader },

  data() {
    return {
      title: 'Email Logs',
      logs: [],
      isLoading: false,
      searchTerm: '',
      currentPage: 1,
      perPage: 15,
      totalLogs: 0,
      tableColumns: [
        { key: 'email_title', label: 'Email' },
        { key: 'sent_to_email', label: 'Recipient' },
        { key: 'subject', label: 'Subject' },
        { key: 'status', label: 'Status' },
        { key: 'created_at', label: 'Sent At' }
      ]
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Email Configuration', to: { name: 'email-config-list' } },
        { text: 'Email Logs', active: true }
      ];
    }
  },

  methods: {
    ...mapActions('emailConfig', ['getEmailLogs']),

    formatDate(value) {
      return value ? moment(value).format('MMM D, YYYY h:mm A') : '-';
    },

    statusBadgeClass(status) {
      if (status === 'sent') return 'badge-success';
      if (status === 'failed') return 'badge-danger';
      return 'badge-secondary';
    },

    async fetchLogs() {
      this.isLoading = true;
      try {
        const params = {
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ordering: '-created_at',
          ...(this.searchTerm && { search: this.searchTerm })
        };
        const res = await this.getEmailLogs(params);
        const payload = res.data || {};
        this.logs = payload.results || [];
        this.totalLogs = payload.count || this.logs.length;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onSearch: debounce(function() {
      this.currentPage = 1;
      this.fetchLogs();
    }, 400),

    onPageChange(page) {
      this.currentPage = page;
      this.$nextTick(this.fetchLogs);
    }
  },

  mounted() {
    this.fetchLogs();
  }
};
</script>
